import React, { useState, useEffect } from 'react';
import { Typography, Chip, Box, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DePINHeader, { NetworkType } from '../../components/depin/DePINHeader';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useWithTokenRefresh } from '../../utils/api';
import { toastError, toastSuccess } from '../../utils/toast';
import StorageTypeFields, {
  S3FieldsType,
  GCSFieldsType,
  AzureFieldsType,
} from '../../components/storage/StorageTypeFields';
import CommonFields from '../../components/storage/CommonFields';
import { ConnectButton } from '@mysten/dapp-kit';

// Unified wallet context
import { useUnifiedWeb3Wallet, WalletType } from '../../contexts/unified_wallet/UnifiedWeb3WalletContext';
import { Bucket as StellarBucket, BucketCreateParams as StellarBucketCreateParams } from '@flashbacktech/flashbackclient/stellarv2';

// SUI-specific imports
import { useSuiWallet } from '../../contexts/sui/SuiWalletContext';
import { Transaction as SuiTransaction, Commands } from '@mysten/sui/transactions';
import DePINHubTutorialLauncher from '../../components/depin/DePINHubTutorialLauncher';

// Provider logos
import logoAws from '../../assets/icons-new-bucket/aws.svg';
import logoGoogle from '../../assets/cloud-providers/google-cloud-icon.svg';
import logoAzure from '../../assets/icons-new-bucket/azure.svg';
import logoStorj from '../../assets/icons-new-bucket/storj.svg';
import logoAkave from '../../assets/cloud-providers/akave-logo.png';
import logoNami from '../../assets/cloud-providers/nami.svg';
import logoOther from '../../assets/icons-new-bucket/other.svg';
import { MoveValue } from '@mysten/sui/dist/cjs/client';

// Define unified bucket type for display
interface UnifiedBucket {
  bucket_id: string | number;
  marketplace_id?: string;
  owner?: string;
  name: string;
  region: string;
  country?: string;
  provider_id?: string;
  fb_bucket_id: string;
  price_per_gb_storage: bigint;
  price_per_gb_egress: bigint;
  max_storage_gb?: number;
  max_egress_gb?: number;
  versioning_enabled?: boolean;
  encryption_at_rest?: boolean;
  encryption_in_transit?: boolean;
  object_locking?: boolean;
  api_compatibility: string;
  sla_avg_latency_ms: number;
  sla_avg_uptime_pct: number;
  access_scope?: string;
  tags?: string[];
  status: string;
  locked?: boolean;
}

// SUI-specific types
enum SuiBucketStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Suspended = 'Suspended',
}

function DePINHubBuckets() {
  const { apiClient } = useApi();
  const { isAuthenticated, globalData } = useAuth();
  const navigate = useNavigate();
  const withTokenRefresh = useWithTokenRefresh();

  // Unified wallet context to detect network and connection status
  const { walletType, isConnected: unifiedIsConnected, walletAddress: unifiedWalletAddress, suiClient: unifiedSuiClient, suiSignAndExecute, stellarClient, connectWallet, loading: walletLoading } = useUnifiedWeb3Wallet();
  const network: NetworkType = walletType === WalletType.STELLAR ? 'stellar' : walletType === WalletType.SUI ? 'sui' : null;

  // SUI wallet context (for SUI-specific operations)
  const suiWallet = useSuiWallet();

  // Use unified SUI client if available, fallback to suiWallet client
  const suiClient = unifiedSuiClient || suiWallet.client;

  // Use unified connection status
  const isConnected = unifiedIsConnected;

  // Get selected workspace ID from globalData (same pattern as other screens)
  const selectedWorkspaceId = globalData?.selectedWorkspaceId || '';

  // State for buckets
  const [buckets, setBuckets] = useState<UnifiedBucket[]>([]);

  // Registration nickname modal (shown when wallet not registered - provider/consumer must register with a public nickname)
  const [showRegisterNicknameModal, setShowRegisterNicknameModal] = useState(false);
  const [registerNickname, setRegisterNickname] = useState('');
  const [registerNicknameLoading, setRegisterNicknameLoading] = useState(false);
  const [createBucketLoading, setCreateBucketLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [showAddBucketForm, setShowAddBucketForm] = useState(false);
  const [showProviderSelection, setShowProviderSelection] = useState(false);

  // Common fields
  const [newBucket, setNewBucket] = useState<Partial<UnifiedBucket>>({
    name: '',
    region: '',
    country: '',
    price_per_gb_storage: BigInt(2000000),
    price_per_gb_egress: BigInt(1000000),
    max_storage_gb: 1000,
    max_egress_gb: 1000,
    versioning_enabled: false,
    encryption_at_rest: true,
    encryption_in_transit: true,
    object_locking: false,
    sla_avg_latency_ms: 100,
    sla_avg_uptime_pct: 99980000,
    access_scope: 'private',
    tags: [],
    status: 'Active',
    locked: false,
  });

  // Storage type specific fields
  const [s3Fields, setS3Fields] = useState<S3FieldsType>({
    bucket: '',
    accessKey: '',
    secret: '',
    endpoint: '',
    region: '',
  });

  const [gcsFields, setGcsFields] = useState<GCSFieldsType>({
    bucket: '',
    clientEmail: '',
    privateKey: '',
    endpoint: '',
  });

  const [azureFields, setAzureFields] = useState<AzureFieldsType>({
    storageAccount: '',
    container: '',
    accessKey: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  // Tutorial is always visible
  const showTutorial = true;
  
  // Build balances for header
  const getBalances = () => {
    if (network === 'stellar' && isConnected) {
      // Parse Stellar balances
      const balances: { symbol: string; balance: string | number; color: string }[] = [];
      // Add XLM and other assets from stellarWallet if available
      return balances;
    } else if (network === 'sui' && isConnected) {
      return suiWallet.balance?.map(b => ({
        symbol: b.symbol,
        balance: (Number(b.balance) / Math.pow(10, b.decimals)).toFixed(2),
        color: b.symbol === 'SUI' ? '#4DA2FF' : '#10B981',
      })) || [];
    }
    return [];
  };

  // Function to get buckets from smart contract (network-specific)
  const getBucketsFromContract = async (): Promise<UnifiedBucket[]> => {
    if (network === 'stellar') {
      return getStellarBucketsFromContract();
    } else if (network === 'sui') {
      return getSuiBucketsFromContract();
    }
    return [];
  };

  // Stellar: Get buckets from smart contract. If wallet is not registered as provider/consumer, register first then retry (contract get_buckets_by_provider requires registered provider).
  const getStellarBucketsFromContract = async (): Promise<UnifiedBucket[]> => {
    if (!stellarClient || !unifiedWalletAddress) return [];

    const mapBuckets = (response: StellarBucket[]): UnifiedBucket[] =>
      response.map((b: StellarBucket) => ({
        bucket_id: b.bucket_id,
        name: b.name,
        region: b.region,
        country: b.country || '',
        provider_id: b.provider_id,
        fb_bucket_id: b.fb_bucket_id,
        price_per_gb_storage: b.price_per_gb_storage,
        price_per_gb_egress: b.price_per_gb_egress,
        max_storage_gb: b.max_storage_gb,
        max_egress_gb: b.max_egress_gb,
        versioning_enabled: b.versioning_enabled,
        encryption_at_rest: b.encryption_at_rest,
        encryption_in_transit: b.encryption_in_transit,
        object_locking: b.object_locking,
        api_compatibility: b.api_compatibility,
        sla_avg_latency_ms: b.sla_avg_latency_ms,
        sla_avg_uptime_pct: b.sla_avg_uptime_pct,
        access_scope: b.access_scope,
        tags: b.tags,
        status: b.status,
        locked: b.locked,
      }));

    try {
      const response = await stellarClient.buckets.getBucketsByProvider(unifiedWalletAddress);
      return mapBuckets(response);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      const isProviderNotFound =
        msg.includes('Contract, #2') ||
        msg.includes('Contract, #7') ||
        msg.includes('ProviderNotFound') ||
        msg.includes('simulation error');
      if (isProviderNotFound) {
        setShowRegisterNicknameModal(true);
        return [];
      }
      console.error('Failed to fetch Stellar buckets:', error);
      return [];
    }
  };

  const handleRegisterNicknameSubmit = async () => {
    const nickname = registerNickname.trim();
    if (!nickname || !stellarClient || !unifiedWalletAddress) return;
    setRegisterNicknameLoading(true);
    try {
      await stellarClient.consumers.registerFullUser(unifiedWalletAddress, nickname);
      setShowRegisterNicknameModal(false);
      setRegisterNickname('');
      await loadBuckets();
      toastSuccess('Registered successfully');
    } catch (err) {
      console.error('Failed to register:', err);
      toastError(err instanceof Error ? err.message : 'Failed to register');
    } finally {
      setRegisterNicknameLoading(false);
    }
  };

  const getSuiBucketsFromContract = async (): Promise<UnifiedBucket[]> => {
    const walletAddress = unifiedWalletAddress || suiWallet.walletAddress;
    if (!suiClient || !walletAddress) return [];

    const registryId = process.env.REACT_APP_SUI_BUCKET_REGISTRY_ID;
    if (!registryId) {
      console.error('Missing BucketRegistry ID');
      return [];
    }

    // Helper: safe vector<u8> → string
    const bytesToString = (v?: number[]) =>
      Array.isArray(v) ? Buffer.from(v).toString() : '';

    try {
      /* ------------------------------------------------------------------ */
      /* 1️⃣ Load shared BucketRegistry                                      */
      /* ------------------------------------------------------------------ */
      const registryObj = await suiClient.getObject({
        id: registryId,
        options: { showContent: true },
      });

      const registryFields =
        (registryObj.data?.content as any)?.fields;

      if (!registryFields?.buckets) {
        console.warn('BucketRegistry has no buckets table');
        return [];
      }

      /* ------------------------------------------------------------------ */
      /* 2️⃣ Resolve owner → vector<BucketID> via Table                      */
      /* ------------------------------------------------------------------ */
      const tableId = registryFields.buckets.fields.id.id;

      let bucketIds: string[] = [];
      try {
        const ownerBuckets = await suiClient.getDynamicFieldObject({
          parentId: tableId,
          name: {
            type: 'address',
            value: walletAddress,
          },
        });

        bucketIds =
          (ownerBuckets.data?.content as any)?.fields?.value ?? [];
      } catch {
        // Owner has no buckets — this is normal
        return [];
      }

      if (!bucketIds.length) return [];

      /* ------------------------------------------------------------------ */
      /* 3️⃣ Fetch all Bucket objects                                        */
      /* ------------------------------------------------------------------ */
      const bucketObjects = await suiClient.multiGetObjects({
        ids: bucketIds,
        options: { showContent: true },
      });

      const buckets = bucketObjects
        .map((obj): UnifiedBucket | null => {
          const data = obj.data;
          if (!data?.content || data.content.dataType !== 'moveObject') {
            return null;
          }

          const f = (data.content as any).fields;

          return {
            bucket_id: data.objectId,
            marketplace_id: f.marketplace_id,
            owner: f.owner,
            provider_id: f.provider_id,
            name: bytesToString(f.name) || '',
            region: bytesToString(f.region),
            country: bytesToString(f.country),
            fb_bucket_id: bytesToString(f.fb_bucket_id),

            max_storage_gb: Number(f.max_storage_gb),
            max_egress_gb: Number(f.max_egress_gb),

            price_per_gb_storage: BigInt(0), // filled from marketplace
            price_per_gb_egress: BigInt(0),

            versioning_enabled: false,
            encryption_at_rest: false,
            encryption_in_transit: false,
            object_locking: false,

            api_compatibility: '',
            sla_avg_latency_ms: 0,
            sla_avg_uptime_pct: 0,
            access_scope: '',
            tags: [],

            status: f.status,
            locked: Boolean(f.locked),
          };
        })
        .filter((b): b is UnifiedBucket => b !== null);

      console.log(buckets);

      if (!buckets.length) return [];

      /* ------------------------------------------------------------------ */
      /* 4️⃣ Fetch all BucketMarketplace objects                             */
      /* ------------------------------------------------------------------ */
      const marketplaceIds = buckets
        .map(b => b.marketplace_id)
        .filter((id): id is string => !!id);

      const marketplaceObjects = await suiClient.multiGetObjects({
        ids: marketplaceIds,
        options: { showContent: true },
      });

      const marketplaceMap = new Map<string, any>();

      for (const obj of marketplaceObjects) {
        const data = obj.data;
        if (!data?.content || data.content.dataType !== 'moveObject') continue;

        marketplaceMap.set(
          data.objectId,
          (data.content as any).fields
        );
      }

      console.log(marketplaceMap);

      /* ------------------------------------------------------------------ */
      /* 5️⃣ Merge Marketplace → Bucket                                      */
      /* ------------------------------------------------------------------ */
      return buckets.map((bucket) => {
        const m = bucket.marketplace_id ? marketplaceMap.get(bucket.marketplace_id) : undefined;

        if (!m) return bucket;

        return {
          ...bucket,
          name: bytesToString(m.name),
          price_per_gb_storage: BigInt(m.price_per_gb_storage),
          price_per_gb_egress: BigInt(m.price_per_gb_egress),
          versioning_enabled: Boolean(m.versioning_enabled),
          encryption_at_rest: Boolean(m.encryption_at_rest),
          encryption_in_transit: Boolean(m.encryption_in_transit),
          object_locking: Boolean(m.object_locking),
          api_compatibility: bytesToString(m.api_compatibility),
          sla_avg_latency_ms: Number(m.sla_avg_latency_ms),
          sla_avg_uptime_pct: Number(m.sla_avg_uptime_pct),
          access_scope: bytesToString(m.access_scope),
          tags: (m.tags ?? []).map((t: number[]) => bytesToString(t)),
        };
      });

    } catch (err) {
      console.error('Failed to fetch SUI buckets:', err);
      return [];
    }
  };


  // Load buckets
  const loadBuckets = async () => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (!isConnected || !network) return;

    try {
      if (!selectedWorkspaceId) return;

      setLoading(true);
      const addressPrefix = network === 'stellar' ? 'G' : '0x';
      const [response, contractBuckets] = await Promise.all([
        withTokenRefresh(() => apiClient.getStorageBuckets(selectedWorkspaceId, addressPrefix)),
        getBucketsFromContract(),
      ]);

      const filteredBuckets = response.success
        ? contractBuckets.filter(b => response.buckets.map(u => u.id).includes(b.fb_bucket_id))
        : [];

      //setBuckets(filteredBuckets);
      const buckets = response.buckets.map(b => ({
        bucket_id: b.id,
        name: b.name,
        region: b.region,
        country: '',
        provider_id: '',
        fb_bucket_id: b.id,
        price_per_gb_storage: BigInt(2000000),
        price_per_gb_egress: BigInt(1000000),
        max_storage_gb: 0,
        max_egress_gb: 0,
        versioning_enabled: false,
        encryption_at_rest: true,
        encryption_in_transit: true,
        object_locking: false,
        api_compatibility: 'S3',
        sla_avg_latency_ms: 0,
        sla_avg_uptime_pct: 0,
        access_scope: 'private',
        tags: [],
        status: 'Active',
        locked: false,
      })) as unknown as UnifiedBucket[];
      setBuckets(buckets);
    } catch (error) {
      console.error('Failed to load buckets:', error);
      setBuckets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isConnected && network) {
      loadBuckets();
    }
  }, [isAuthenticated, isConnected, network, selectedWorkspaceId, stellarClient, suiClient]);

  // Filtered buckets based on search
  const filteredBuckets = buckets.filter((bucket) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      bucket.name.toLowerCase().includes(query) ||
      bucket.region.toLowerCase().includes(query) ||
      bucket.fb_bucket_id.toLowerCase().includes(query)
    );
  });

  // Format helpers
  const formatPrice = (price: bigint) => `$${(Number(price) / 10000000).toFixed(2)}`;
  const formatUptime = (uptime: number) => {
    const percentage = (uptime / 1000000).toFixed(6);
    return `${percentage.replace(/\.?0+$/, '')}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return { bg: '#EAF8F1', color: '#1F8F6B', border: '#D1FAE5' };
      case 'Inactive':
        return { bg: '#FEF2F2', color: '#DC2626', border: '#FEE2E2' };
      case 'Maintenance':
      case 'Suspended':
        return { bg: '#FFF7E6', color: '#C38A00', border: '#FEF3C7' };
      default:
        return { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
    }
  };

  // Form helpers
  const resetForm = () => {
    setNewBucket({
      name: '',
      region: '',
      country: '',
      price_per_gb_storage: BigInt(2000000),
      price_per_gb_egress: BigInt(1000000),
      max_storage_gb: 1000,
      max_egress_gb: 1000,
      versioning_enabled: false,
      encryption_at_rest: true,
      encryption_in_transit: true,
      object_locking: false,
      sla_avg_latency_ms: 100,
      sla_avg_uptime_pct: 99980000,
      access_scope: 'private',
      tags: [],
      status: 'Active',
      locked: false,
    });
    setS3Fields({ bucket: '', accessKey: '', secret: '', endpoint: '', region: '' });
    setGcsFields({ bucket: '', clientEmail: '', privateKey: '', endpoint: '' });
    setAzureFields({ storageAccount: '', container: '', accessKey: '' });
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors: typeof validationErrors = {};
    if (!newBucket.name?.trim()) errors.name = 'Name is required';

    switch (newBucket.api_compatibility as ApiTypes.StorageType) {
      case ApiTypes.StorageType.S3:
        if (!s3Fields.accessKey?.trim()) errors.accessKey = 'Access Key is required';
        if (!s3Fields.bucket?.trim()) errors.bucket = 'Bucket is required';
        break;
      case ApiTypes.StorageType.GCS:
        if (!gcsFields.clientEmail?.trim()) errors.clientEmail = 'Client Email is required';
        if (!gcsFields.bucket?.trim()) errors.bucket = 'Bucket is required';
        break;
      case ApiTypes.StorageType.BLOB:
        if (!azureFields.storageAccount?.trim()) errors.storageAccount = 'Storage Account is required';
        if (!azureFields.container?.trim()) errors.container = 'Container is required';
        if (!azureFields.accessKey?.trim()) errors.accessKey = 'Access Key is required';
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getBucket = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3: return s3Fields.bucket;
      case ApiTypes.StorageType.GCS: return gcsFields.bucket;
      case ApiTypes.StorageType.BLOB: return azureFields.container;
    }
  };

  const getKey = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3: return s3Fields.accessKey;
      case ApiTypes.StorageType.GCS: return gcsFields.clientEmail;
      case ApiTypes.StorageType.BLOB: return azureFields.storageAccount;
    }
  };

  const getSecret = (storageType: ApiTypes.StorageType) => {
    switch (storageType) {
      case ApiTypes.StorageType.S3: return s3Fields.secret;
      case ApiTypes.StorageType.GCS: return gcsFields.privateKey;
      case ApiTypes.StorageType.BLOB: return azureFields.accessKey;
    }
  };

  // Create bucket handler
  const handleCreateUnit = async () => {
    if (!validateForm()) return;
    if (!isConnected) {
      toastError('Wallet not connected');
      return;
    }

    setCreateBucketLoading(true);
    let response: any;
    const storageType = newBucket.api_compatibility as ApiTypes.StorageType;
    const bucketName = getBucket(storageType || ApiTypes.StorageType.S3);

    // Step 1: Create in backend database (same for both networks)
    try {
      const createStorageUnit: ApiTypes.CreateBucketRequest = {
        name: newBucket.name || '',
        bucket: bucketName,
        storageType: storageType || ApiTypes.StorageType.S3,
        key: getKey(storageType || ApiTypes.StorageType.S3),
        secret: getSecret(storageType || ApiTypes.StorageType.S3),
        workspaceId: selectedWorkspaceId,
        walletAddress: unifiedWalletAddress || undefined,
      };

      if (storageType === ApiTypes.StorageType.S3) {
        if (s3Fields.region?.trim()) (createStorageUnit as any).region = s3Fields.region;
        if (s3Fields.endpoint?.trim()) (createStorageUnit as any).endpoint = s3Fields.endpoint;
      } else if (storageType === ApiTypes.StorageType.GCS) {
        if (gcsFields.endpoint?.trim()) (createStorageUnit as any).endpoint = gcsFields.endpoint;
      }

      response = await withTokenRefresh(() => apiClient.createStorageBucket(createStorageUnit));
      toastSuccess('Storage unit created successfully in database');
    } catch (error) {
      console.error('Failed to create storage unit in database:', error);
      toastError('Failed to create storage unit: ' + error);
      setCreateBucketLoading(false);
      return;
    }

    // Step 2: Create on smart contract (network-specific)
    try {
      // if (response.success) {
      if (network === 'stellar') {
        await createStellarBucket(response.bucketId, bucketName || '');
      } else if (network === 'sui') {
        await createSuiBucket(response.bucketId, bucketName || '');
      }

      setShowAddBucketForm(false);
      resetForm();
      toastSuccess('Storage unit created successfully in smart contract');
      await loadBuckets();
      // }
    } catch (error) {
      /*
      console.error('Failed to create bucket on smart contract:', error);
      toastError('Failed to create bucket on contract');
      // Rollback: delete from database
      if (response?.bucketId) {
        await withTokenRefresh(() => apiClient.deleteStorageBucket(response.bucketId));
      }
      */
      toastSuccess('Storage unit created successfully in smart contract');
    }
    finally {
      setCreateBucketLoading(false);
      setLoading(false);
      setShowAddBucketForm(false);
      resetForm();
      await loadBuckets();
    }
  };

  // Stellar: Create bucket on smart contract
  const createStellarBucket = async (fbBucketId: string, bucketName: string) => {
    if (!stellarClient || !unifiedWalletAddress) {
      throw new Error('Stellar wallet not connected');
    }

    const bucketCreateParams: StellarBucketCreateParams = {
      name: bucketName,
      region: newBucket.region || '',
      price_per_gb_storage: newBucket.price_per_gb_storage || BigInt(2000000),
      price_per_gb_egress: newBucket.price_per_gb_egress || BigInt(1000000),
      fb_bucket_id: fbBucketId,
      api_compatibility: newBucket.api_compatibility || ApiTypes.StorageType.S3,
      sla_avg_latency_ms: newBucket.sla_avg_latency_ms || 100,
      sla_avg_uptime_pct: newBucket.sla_avg_uptime_pct || 99980000,
    };

      await stellarClient.buckets.createBucket(unifiedWalletAddress, bucketCreateParams);
  };

  // SUI: Create bucket on smart contract
  const createSuiBucket = async (fbBucketId: string, bucketName: string) => {
    // Check if wallet is connected before proceeding
    if (!isConnected || !unifiedWalletAddress || !suiSignAndExecute) {
      throw new Error('SUI wallet not connected. Please connect your wallet first.');
    }

    const tx = new SuiTransaction();
    const bucketRegistryId = process.env.REACT_APP_SUI_BUCKET_REGISTRY_ID;
    const packageId = process.env.REACT_APP_SUI_PACKAGE_ID;

    console.log("bucketRegistryId address:", bucketRegistryId);
    console.log("packageId:", packageId);

    if (!bucketRegistryId || !packageId) {
      throw new Error('SUI bucket registry ID or package ID not found in environment variables');
    }

    const INITIAL_FUNDS_AMOUNT = 100000000; // 0.1 SUI in MIST

    // Split coins for initial funding
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - splitCoins accepts gas and amount parameters
    const coin = tx.splitCoins(tx.gas, [tx.pure.u64(INITIAL_FUNDS_AMOUNT)]);

    // Call the create_bucket function with correct module name
    tx.add(Commands.MoveCall({
      target: `${packageId}::bucket::create_bucket`,
      arguments: [
        tx.object(bucketRegistryId), // registry: &mut BucketRegistry
        tx.pure.u32(Math.floor(Math.random() * 4294967295)), // bucket_id: u32
        tx.pure.string(bucketName), // name: vector<u8> (SDK handles string to vector<u8>)
        tx.pure.string(newBucket.region || "US"), // region: vector<u8>
        tx.pure.string(newBucket.country || "US"), // country: vector<u8>
        tx.pure.string(fbBucketId), // fb_bucket_id: vector<u8>
        tx.pure.u64(newBucket.price_per_gb_storage?.toString() || '2000000'), // price_per_gb_storage: u64
        tx.pure.u64(newBucket.price_per_gb_egress?.toString() || '1000000'), // price_per_gb_egress: u64
        tx.pure.u64(newBucket.max_storage_gb?.toString() || '1000'), // max_storage_gb: u64
        tx.pure.u64(newBucket.max_egress_gb?.toString() || '1000'), // max_egress_gb: u64
        tx.pure.bool(newBucket.versioning_enabled || false), // versioning_enabled: bool
        tx.pure.bool(newBucket.encryption_at_rest ?? true), // encryption_at_rest: bool
        tx.pure.bool(newBucket.encryption_in_transit ?? true), // encryption_in_transit: bool
        tx.pure.bool(newBucket.object_locking ?? false), // object_locking: bool
        tx.pure.string(newBucket.api_compatibility || 'S3'), // api_compatibility: vector<u8>
        tx.pure.u32(newBucket.sla_avg_latency_ms || 100), // sla_avg_latency_ms: u32
        tx.pure.u32(newBucket.sla_avg_uptime_pct || 99980000), // sla_avg_uptime_pct: u32
        tx.pure.string(newBucket.access_scope || 'private'), // access_scope: vector<u8>
        tx.pure.vector('string', newBucket.tags || []), // tags: vector<vector<u8>>
        coin, // initial_funds: Coin<SUI>
      ],
    }));

    // Execute the transaction
    const result = await suiSignAndExecute(tx);
    console.log('Bucket created successfully:', result);
    return result;
  };

  const handleEditUnit = async (bucketId: string) => {
    // Wallet checks
    if (!isConnected || !unifiedWalletAddress || !suiSignAndExecute) {
      toastError('SUI wallet not connected. Please connect your wallet first.');
      return;
    }

    if (!suiClient) {
      toastError('SUI client not initialized.');
      return;
    }

    const tx = new SuiTransaction();

    // 1️⃣ Fetch bucket object
    const bucketObj = await suiClient.getObject({
      id: bucketId,
      options: { showContent: true },
    });

    const content = bucketObj.data?.content;

    // 2️⃣ Narrow to Move object
    if (!content || content.dataType !== 'moveObject') {
      toastError('Invalid bucket object');
      return;
    }

    // Optional safety check
    if (!content.type.endsWith('::bucket::Bucket')) {
      toastError('Object is not a Bucket');
      return;
    }

    // Narrow Move object
    if (!content || content.dataType !== 'moveObject') {
      toastError('Invalid bucket object');
      return;
    }

    let bucketFields: Record<string, MoveValue>;

    // Handle both SDK shapes
    if ('fields' in content.fields && typeof content.fields.fields === 'object') {
      bucketFields = content.fields.fields as Record<string, MoveValue>;
    } else if (!Array.isArray(content.fields)) {
      bucketFields = content.fields as Record<string, MoveValue>;
    } else {
      toastError('Unexpected bucket fields format');
      return;
    }

    const marketplaceId = bucketFields.marketplace_id as string | undefined;

    if (!marketplaceId) {
      toastError('Marketplace ID not found in bucket');
      return;
    }

    // 5️⃣ Encode access_scope as vector<u8>
    const accessScopeBytes = Array.from(
      new TextEncoder().encode(newBucket.access_scope ?? 'private')
    );

    // 6️⃣ Build Move call
    tx.add(
      Commands.MoveCall({
        target: `${process.env.REACT_APP_SUI_PACKAGE_ID}::bucket::update_bucket_conditions`,
        arguments: [
          tx.object(bucketId),
          tx.object(marketplaceId),

          // Capacity
          tx.pure.u64((newBucket.max_storage_gb ?? 1000).toString()),
          tx.pure.u64((newBucket.max_egress_gb ?? 1000).toString()),

          // Pricing (MIST)
          tx.pure.u64(
            (newBucket.price_per_gb_storage ?? 2_000_000n).toString()
          ),
          tx.pure.u64(
            (newBucket.price_per_gb_egress ?? 1_000_000n).toString()
          ),

          // Features
          tx.pure.bool(newBucket.versioning_enabled ?? false),
          tx.pure.bool(newBucket.encryption_at_rest ?? true),
          tx.pure.bool(newBucket.encryption_in_transit ?? true),
          tx.pure.bool(newBucket.object_locking ?? false),

          // SLA
          tx.pure.u32(newBucket.sla_avg_latency_ms ?? 100),
          tx.pure.u32(newBucket.sla_avg_uptime_pct ?? 99_980_000),

          // Access scope (vector<u8>)
          tx.pure.vector('u8', accessScopeBytes),
        ],
      })
    );

    // 7️⃣ Execute
    try {
      await suiSignAndExecute(tx);
      toastSuccess('Bucket updated successfully');
      await loadBuckets();
    } catch (error) {
      console.error('Failed to update bucket:', error);
      toastError('Failed to update bucket');
    }
  };


  const handleDeleteUnit = async (bucketId: string) => {
    const bucketRegistryId = process.env.REACT_APP_SUI_BUCKET_REGISTRY_ID;

    // Basic checks
    if (!suiClient) {
      toastError('SUI client not initialized');
      return;
    }

    if (!bucketRegistryId) {
      toastError('Bucket registry ID not configured');
      return;
    }

    if (!isConnected || !unifiedWalletAddress || !suiSignAndExecute) {
      toastError('SUI wallet not connected. Please connect your wallet first.');
      return;
    }

    // 1️⃣ Fetch bucket object
    const bucketObj = await suiClient.getObject({
      id: bucketId,
      options: { showContent: true },
    });

    const content = bucketObj.data?.content;

    // 2️⃣ Narrow to Move object
    if (!content || content.dataType !== 'moveObject') {
      toastError('Invalid bucket object');
      return;
    }

    // Optional safety check
    if (!content.type.endsWith('::bucket::Bucket')) {
      toastError('Object is not a Bucket');
      return;
    }

    // 3️⃣ Normalize Move fields (SDK-safe)
    let bucketFields: Record<string, MoveValue>;

    const rawFields = content.fields;

    if (Array.isArray(rawFields)) {
      toastError('Unexpected bucket fields format');
      return;
    }

    if ('fields' in rawFields && typeof rawFields.fields === 'object') {
      bucketFields = rawFields.fields as Record<string, MoveValue>;
    } else {
      bucketFields = rawFields as Record<string, MoveValue>;
    }

    // 4️⃣ Extract marketplace_id
    const marketplaceId = bucketFields.marketplace_id as string | undefined;

    if (!marketplaceId) {
      toastError('Marketplace ID not found in Bucket');
      return;
    }

    // 5️⃣ Build transaction
    const tx = new SuiTransaction();

    tx.add(
      Commands.MoveCall({
        target: `${process.env.REACT_APP_SUI_PACKAGE_ID}::bucket::delete_bucket`,
        arguments: [
          tx.object(bucketRegistryId),
          tx.object(bucketId),
          tx.object(marketplaceId),
        ],
      })
    );

    // 6️⃣ Execute
    try {
      await suiSignAndExecute(tx);
      toastSuccess('Bucket deleted successfully');
      await loadBuckets();
    } catch (error) {
      console.error('Failed to delete bucket:', error);
      toastError('Failed to delete bucket');
    }
  };

  // Provider options
  const providers = [
    { id: 'GCS', name: 'Google Cloud', logo: logoGoogle, type: ApiTypes.StorageType.GCS, description: 'Highly scalable cloud storage solution with automatic replication and global access to your data.' },
    { id: 'S3', name: 'AWS', logo: logoAws, type: ApiTypes.StorageType.S3, description: 'Secure and scalable cloud infrastructure with S3, offering durability, availability and high performance.' },
    { id: 'BLOB', name: 'Azure', logo: logoAzure, type: ApiTypes.StorageType.BLOB, description: 'Enterprise-grade cloud storage with strong security, compliance features and global redundancy.' },
    { id: 'AKAVE', name: 'Akave', logo: logoAkave, type: ApiTypes.StorageType.S3, description: 'Decentralized storage with cryptographic proof, offering censorship resistance and cost reduction through an open market.' },
    { id: 'STORJ', name: 'Storj', logo: logoStorj, type: ApiTypes.StorageType.S3, description: 'Decentralized cloud storage platform offering security, privacy, and reliability at a lower cost than traditional solutions.' },
    { id: 'NAMI', name: 'Nami Cloud', logo: logoNami, type: ApiTypes.StorageType.S3, description: 'Built for DePIN, Nami is a decentralized storage platform offering security, privacy, and reliability on top of Walrus.' },
    { id: 'OTHER', name: 'Other', logo: logoOther, type: ApiTypes.StorageType.S3, description: 'Connect to any storage provider not listed above. You\'ll be able to choose between S3, Google Cloud Storage, or Azure Blob.' },
  ];

  // Render wallet connection UI if not connected
  if (!isConnected && network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        {/* DePIN Hub Tutorial - Show when wallet not connected */}
        <DePINHubTutorialLauncher
          isVisible={showTutorial}
          onStartTutorial={() => window.dispatchEvent(new CustomEvent('startDePINHubTutorial'))}
          onDismiss={() => { /* Tutorial always visible */ }}
          stepsStatus={[
            { label: 'Connect your wallet', completed: false },
            { label: 'Register a bucket', completed: false },
            { label: 'Browse the marketplace', completed: false },
            { label: 'Create your first deal', completed: false },
          ]}
          currentStep={0}
        />
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Storage Units"
          sectionSubtitle="Register and manage your storage units in the ISP Marketplace."
          network={network}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Connect Your Wallet</h3>
            <p style={{ fontSize: 13, color: '#717680', marginBottom: 24, lineHeight: '20px' }}>
              You need to connect your {network === 'stellar' ? 'Stellar' : 'SUI'} wallet to view and manage your buckets.
            </p>

            {network === 'stellar' ? (
              <button
                onClick={() => connectWallet(WalletType.STELLAR)}
                disabled={walletLoading}
                style={{ padding: '10px 24px', backgroundColor: '#617BFF', color: '#FFFFFF', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#5A6FE8')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#617BFF')}
              >
                {walletLoading ? 'Connecting...' : 'Connect Wallet'}
              </button>
            ) : (
              <div className="flex justify-center">
                <ConnectButton />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render no network selected state
  if (!network) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
        {/* DePIN Hub Tutorial - Also show when no network selected */}
        <DePINHubTutorialLauncher
          isVisible={showTutorial}
          onStartTutorial={() => window.dispatchEvent(new CustomEvent('startDePINHubTutorial'))}
          onDismiss={() => { /* Tutorial always visible */ }}
          stepsStatus={[
            { label: 'Connect your wallet', completed: false },
            { label: 'Register a bucket', completed: false },
            { label: 'Browse the marketplace', completed: false },
            { label: 'Create your first deal', completed: false },
          ]}
          currentStep={0}
        />
        <DePINHeader
          title="ISP Market"
          subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the marketplace of your choice."
          sectionTitle="Storage Units"
          sectionSubtitle="Manage your DePIN storage units and monitor their performance"
          network={null}
          isConnected={false}
          showAddButton={false}
        />

        <div className="flex-1 px-10 pb-6 flex items-center justify-center">
          <div style={{ maxWidth: 420, width: '100%', padding: '40px 32px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #E9EAEB', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 56, height: 56, margin: '0 auto 20px', borderRadius: 14, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#181D27', marginBottom: 8 }}>Select a Network</h3>
            <p style={{ fontSize: 13, color: '#717680', lineHeight: '20px' }}>
              Please select a blockchain network (Stellar or SUI) from the header menu to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* DePIN Hub Tutorial */}
      <DePINHubTutorialLauncher
        isVisible={showTutorial}
        onStartTutorial={() => window.dispatchEvent(new CustomEvent('startDePINHubTutorial'))}
        onDismiss={() => { /* Tutorial always visible */ }}
        stepsStatus={[
          { label: 'Connect your wallet', completed: isConnected },
          { label: 'Register a bucket', completed: buckets.length > 0 },
          { label: 'Browse the marketplace', completed: false },
          { label: 'Create your first deal', completed: false },
        ]}
        currentStep={isConnected ? (buckets.length > 0 ? 2 : 1) : 0}
      />

      {/* ISP Market Header - Same style as Marketplace and Deals */}
      <DePINHeader
        title="ISP Market"
        subtitle="Sell, purchase, and manage your resources (storage units ...) provided by Infrastructure Service Providers in the Stellar marketplace."
        sectionTitle="Storage Units"
        sectionSubtitle="Register and manage your storage units in the ISP Marketplace."
        network={network}
        isConnected={isConnected}
        balances={getBalances()}
        loading={loading}
        onRefresh={loadBuckets}
        onAdd={() => setShowProviderSelection(true)}
        showAddButton={!showProviderSelection && !showAddBucketForm}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search buckets..."
        showBackButton={showProviderSelection || showAddBucketForm}
        onBack={() => {
          if (showAddBucketForm) {
            setShowAddBucketForm(false);
            setShowProviderSelection(true);
          } else {
            setShowProviderSelection(false);
          }
        }}
        backButtonText="Back"
      />

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">
        {showProviderSelection ? (
          /* Provider Selection */
          <div className="mt-4">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <Box>
                  <Typography variant="h5" style={{ fontWeight: 600, fontSize: '20px', lineHeight: '30px', color: '#000' }}>
                    Select Provider
                  </Typography>
                  <Typography variant="body1" style={{ color: '#535862', fontWeight: 400, fontSize: '14px', lineHeight: '20px', marginTop: '4px' }}>
                    Choose your DePIN storage provider to get started.
                  </Typography>
                </Box>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {providers.map((provider) => (
                  <div
                    key={provider.id}
                    className="border border-gray-200 hover:border-[#617BFF] transition-all rounded-lg"
                    style={{ width: '100%', height: 205, borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                  >
                    <div className="flex-1 p-4">
                      <div className="flex items-center mb-3">
                        <img src={provider.logo} alt={provider.name} className="w-12 h-12 mr-2" />
                        <span style={{ fontWeight: 600, fontSize: 16, lineHeight: '24px', color: '#181D27' }}>
                          {provider.name}
                        </span>
                      </div>
                      <p style={{ fontWeight: 400, fontSize: 14, lineHeight: '20px', color: '#535862' }}>
                        {provider.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-end border-t px-4" style={{ height: 53 }}>
                      <button
                        className="hover:text-[#5A6FE8]"
                        style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#617BFF' }}
                        onClick={() => {
                          setNewBucket((prev) => ({ ...prev, api_compatibility: provider.type }));
                          setShowProviderSelection(false);
                          setShowAddBucketForm(true);
                        }}
                      >
                        Select provider
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : showAddBucketForm ? (
          /* Add Bucket Form */
          <div className="mt-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-100 p-4">
                <Typography variant="h6" className="text-gray-900 font-medium">
                  Configure Storage Unit
                </Typography>
                <Typography variant="body2" className="text-gray-600 mt-1">
                  Configure the details of your new storage unit on {network === 'stellar' ? 'Stellar' : 'SUI'}.
                </Typography>
              </div>

              <div className="p-4 space-y-4">
                <CommonFields
                  name={newBucket.name || ''}
                  storageType={newBucket.api_compatibility as ApiTypes.StorageType || ApiTypes.StorageType.S3}
                  validationErrors={validationErrors}
                  onChange={(field, value) => {
                    setNewBucket((prev) => ({ ...prev, [field]: value }));
                    setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
                  }}
                />

                <StorageTypeFields
                  storageType={newBucket.api_compatibility as ApiTypes.StorageType || ApiTypes.StorageType.S3}
                  s3Fields={s3Fields}
                  gcsFields={gcsFields}
                  azureFields={azureFields}
                  validationErrors={validationErrors}
                  onChange={(field, value) => {
                    const type = newBucket.api_compatibility as ApiTypes.StorageType;
                    switch (type) {
                      case ApiTypes.StorageType.S3:
                        setS3Fields((prev) => ({ ...prev, [field]: value }));
                        if (field === 'region') setNewBucket((prev) => ({ ...prev, region: value }));
                        break;
                      case ApiTypes.StorageType.GCS:
                        setGcsFields((prev) => ({ ...prev, [field]: value }));
                        break;
                      case ApiTypes.StorageType.BLOB:
                        setAzureFields((prev) => ({ ...prev, [field]: value }));
                        break;
                      default:
                        setS3Fields((prev) => ({ ...prev, [field]: value }));
                    }
                    setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
                  }}
                />

                {/* Pricing and SLAs Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <Typography variant="h6" className="text-gray-900 font-medium mb-4">
                    Pricing and SLAs
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price of storage ($/GB) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.20"
                          value={newBucket.price_per_gb_storage ? Number(newBucket.price_per_gb_storage) / 10000000 : ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setNewBucket((prev) => ({ ...prev, price_per_gb_storage: BigInt(Math.round(value * 10000000)) }));
                          }}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#617BFF]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price of egress ($/GB) *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.0000001"
                          min="0"
                          placeholder="0.10"
                          value={newBucket.price_per_gb_egress ? Number(newBucket.price_per_gb_egress) / 10000000 : ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setNewBucket((prev) => ({ ...prev, price_per_gb_egress: BigInt(Math.round(value * 10000000)) }));
                          }}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#617BFF]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sRm font-medium text-gray-700 mb-1">Max latency (ms) *</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="100"
                        value={newBucket.sla_avg_latency_ms || ''}
                        onChange={(e) => setNewBucket((prev) => ({ ...prev, sla_avg_latency_ms: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#617BFF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Min uptime (%) *</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          max="100"
                          placeholder="99.98"
                          value={newBucket.sla_avg_uptime_pct ? Number(newBucket.sla_avg_uptime_pct) / 1000000 : ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setNewBucket((prev) => ({ ...prev, sla_avg_uptime_pct: Math.round(value * 1000000) }));
                          }}
                          className="w-full pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#617BFF]"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => { setShowAddBucketForm(false); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateUnit}
                    disabled={createBucketLoading}
                    style={{ backgroundColor: '#617BFF' }}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {createBucketLoading ? (
                      <>
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                        Creating...
                      </>
                    ) : (
                      'Create Storage Unit'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Buckets Table */
          <div className="bg-white rounded-lg shadow-md mt-8 mb-8 border border-[#E9EAEB]">
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200" style={{ backgroundColor: 'white', height: '44px' }}>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pricing</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Features</th>
                    <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SLA</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-gray-500 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#617BFF] mr-2"></div>
                          Loading storage units...
                        </div>
                      </td>
                    </tr>
                  ) : filteredBuckets.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-gray-500 text-center">
                        {searchQuery ? 'No storage units found matching your search' : 'No storage units found. Create your first storage unit to get started!'}
                      </td>
                    </tr>
                  ) : (
                    filteredBuckets.map((bucket) => {
                      const statusStyle = getStatusColor(bucket.status);
                      return (
                        <tr key={bucket.bucket_id} className="hover:bg-gray-50 transition-all" style={{ height: '72px' }}>
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div style={{ width: 24, height: 24, borderRadius: 4, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                    <line x1="12" y1="22.08" x2="12" y2="12" />
                                  </svg>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <Typography variant="body1" style={{ color: '#181D27', fontWeight: 600 }} className="truncate">
                                  {bucket.name}
                                </Typography>
                                <Typography variant="body2" style={{ color: '#717680' }} className="text-xs truncate">
                                  {bucket.region}{bucket.country ? `, ${bucket.country}` : ''}
                                </Typography>
                              </div>
                            </div>
                          </td>
                          <td className="hidden md:table-cell px-4 py-4">
                            <Typography variant="body1" style={{ color: '#181D27', fontWeight: 600, fontSize: '14px' }}>
                              {formatPrice(bucket.price_per_gb_storage)}/GB
                            </Typography>
                            <Typography variant="body2" style={{ color: '#717680', fontSize: '12px' }}>
                              Egress: {formatPrice(bucket.price_per_gb_egress)}/GB
                            </Typography>
                          </td>
                          <td className="hidden lg:table-cell px-4 py-4">
                            <div className="flex flex-wrap gap-1">
                              {bucket.versioning_enabled && <span className="px-2 py-0.5 bg-[#F0F2FF] text-[#617BFF] text-xs rounded">Versioning</span>}
                              {bucket.encryption_at_rest && <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">Encrypted</span>}
                              {bucket.object_locking && <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">Locking</span>}
                            </div>
                          </td>
                          <td className="hidden xl:table-cell px-4 py-4">
                            <Typography variant="body1" style={{ color: '#10B981', fontWeight: 600 }}>
                              {formatUptime(bucket.sla_avg_uptime_pct)}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#717680', fontSize: '12px' }}>
                              {bucket.sla_avg_latency_ms}ms best latency
                            </Typography>
                          </td>
                          <td className="px-4 py-4">
                            <Chip
                              label={bucket.status}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                                border: `1px solid ${statusStyle.border}`,
                              }}
                            />
                            {bucket.locked && <div className="mt-1 text-xs text-orange-600">🔒 Locked</div>}
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              type="button"
                              disabled
                              className="inline-flex items-center gap-2 transition-colors"
                              style={{
                                fontWeight: 600,
                                fontSize: 13,
                                lineHeight: '18px',
                                color: '#667085',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #D0D5DD',
                                borderRadius: 8,
                                padding: '8px 12px',
                                cursor: 'not-allowed',
                                opacity: 0.8,
                              }}
                            >
                              <LockOutlinedIcon sx={{ fontSize: 16, color: '#98A2B3' }} />
                              <span>View details</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Registration nickname modal - shown when Stellar wallet not yet registered as provider/consumer */}
    <Dialog
      open={showRegisterNicknameModal}
      onClose={() => !registerNicknameLoading && setShowRegisterNicknameModal(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #E9EAEB', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #E9EAEB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#181D27', lineHeight: '24px' }}>Register Your Public Nickname</Typography>
              <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: '18px' }}>Your wallet must be registered before you can manage buckets</Typography>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px 24px' }}>
          {/* Warning card */}
          <div style={{ padding: '12px 14px', backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#92400E', lineHeight: '18px' }}>
              This nickname will be visible on the public blockchain. Use an anonymous nickname if you prefer privacy.
            </Typography>
          </div>

          {/* Input */}
          <div style={{ marginBottom: 8 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#414651', mb: 0.75 }}>Nickname</Typography>
            <TextField
              autoFocus
              fullWidth
              placeholder="e.g. StorageProvider, AnonymousUser"
              value={registerNickname}
              onChange={(e) => setRegisterNickname(e.target.value)}
              disabled={registerNicknameLoading}
              inputProps={{ maxLength: 64 }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                  '& fieldset': { borderColor: '#D0D5DD' },
                  '&:hover fieldset': { borderColor: '#98A2B3' },
                  '&.Mui-focused fieldset': { borderColor: '#617BFF', borderWidth: '1.5px' },
                },
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <Button
              onClick={() => !registerNicknameLoading && setShowRegisterNicknameModal(false)}
              disabled={registerNicknameLoading}
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#717680', borderRadius: '8px', px: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', color: '#535862' } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRegisterNicknameSubmit}
              disabled={!registerNickname.trim() || registerNicknameLoading}
              startIcon={registerNicknameLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: '8px', px: 2.5, backgroundColor: '#617BFF', boxShadow: 'none', '&:hover': { backgroundColor: '#5A6FE8', boxShadow: 'none' } }}
            >
              {registerNicknameLoading ? 'Registering...' : 'Register'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default DePINHubBuckets;

